// Action Types
export const LOGIN_USER = 'auth/loginUser'
export const LOGOUT_USER = 'auth/logoutUser'

// Action Creators
export const loginUser = (payload) => ({ type: LOGIN_USER, payload })
export const logoutUser = () => ({ type: LOGOUT_USER })

// State
export const initialState = {
  first_name: null,
  last_name: null,
  token: null,
  username: null,
  user_id: null,
  organizationid: null,
  organization_name: null,
  edit_config: null,
  edit_users: null,
  edit_leaderboard: null,
  realtime_coaching_access: null,
  org_realtime_coaching_access: null,
  account_leaderboard_visibility: null,
  account_production: null,
  account_rtqa_access: null,
  edit_qa: null,
  edit_qa_copilot: null,
  playbook_name: null,
  uuid: null,
  hierarchy_manager: null,
  metrics_job_function: null,
  own_organization_id: null,
  tools_editor: null,
  balto_cloud_access: null,
}

// Selectors

// Real-Time Coaching product access — the RTC nav item (alert configuration and
// reporting). This is the per-user permission; the live surfaces read
// `hasLiveListenAccess` and the chat surface reads `hasAgentAssistAccess`
// below, neither of which consults it. Hierarchy managers impersonating a child
// org are excluded (org hierarchy isn't supported in RTC yet).
// `real_time_management_access` is the deprecated alias of the user-level
// permission and is still honored.
export const hasRealtimeCoachingAccess = (currentUser) => {
  const {
    realtime_coaching_access: userRealtimeAccess,
    real_time_management_access: deprecatedRealtimeAccess,
    org_realtime_coaching_access: orgRealtimeAccess,
    hierarchy_manager: isHierarchyManager,
    organizationid: currentOrganizationId,
    own_organization_id: ownOrganizationId,
  } = currentUser
  const childOrgSelected = isHierarchyManager && currentOrganizationId !== ownOrganizationId

  return Boolean(
    (userRealtimeAccess || deprecatedRealtimeAccess) && orgRealtimeAccess && !childOrgSelected
  )
}

// The live call surface: the Live Listen page, and inside the Agent Assist
// widget the Listen button and the live transcript. All three are the same
// unredacted call content, so they share one gate.
//
// `showCommandCenter` is not a rollout flag. It is an off switch built for
// Intelogix (orgs 3901/3902), who asked for Live Listen to be disabled BECAUSE
// the live surface is unredacted and they have PCI in scope. So it gates
// content, not a page, and nothing may short-circuit past it — an org that
// bought "no live listen" must not get the same audio and the same words in
// text form through the widget instead.
//
// No per-user permission: it is an org/flag capability, and the page itself
// only redirects on an explicit `false` (`CommandCenterPage`, `IndexRedirect`).
export const hasLiveListenAccess = (currentOrganization = {}, flags = {}) =>
  !currentOrganization.audio_ingestion_only && flags.showCommandCenter !== false

// The chat surface — the Agent Assist widget and its launcher. Chat is
// manager-to-agent text, so it carries none of the call content
// `hasLiveListenAccess` guards and is open to everyone the product reaches.
//
// Deliberately NOT gated on the per-user RTC permission: notification
// subscriptions never were, so a manager sent a raised-hand notification got a
// Chat button wired to a panel that could not mount (QCER-1103). Nor on
// `showCommandCenter`, which would leave that same manager dead-ended in an
// org that only asked to turn off live listen.
//
// Audio-ingestion-only orgs have no live surfaces at all — matching
// `hasRTCAccess` in `Sidebar.helpers.js`.
export const hasAgentAssistAccess = (currentOrganization = {}) =>
  !currentOrganization.audio_ingestion_only

// Kodi entitlement, as resolved by core-service.
//
// `kodi_access` is the ANSWER, not the inputs: core-service computes it with
// the same `is_kodi_enabled` the Kodi service gates on, against the user's HOME
// org. The frontend deliberately does not resolve this itself —
// `users.kodi_enabled` NULL means "inherit the org", and for a hierarchy
// manager the store holds the org being VIEWED rather than the home org whose
// flag governs, so any local resolution disagreed with the server in one
// direction or the other.
//
// Shared by the Kodi nav item and the /kodi guard so the two can't drift — a
// nav item that shows a page which immediately redirects is worse than no nav
// item. Deliberately does NOT include the `baltoCoworkEnabled` LD check: that
// gates Balto's rollout, is read from a different source, and callers combine
// the two themselves.
export const hasKodiEntitlement = (currentUser) => {
  const { kodi_access: kodiAccess } = currentUser

  // undefined means core-service is not serving the field yet, so fail OPEN —
  // failing closed would hide Kodi from every user the moment the web app
  // deploys ahead of the API. Safe because this is only UI: the Kodi service
  // enforces the same entitlement and fails closed.
  if (kodiAccess === undefined) {
    return true
  }

  return Boolean(kodiAccess)
}

// Reducer
export default function currentUserReducer(state = initialState, action) {
  switch (action.type) {
    case LOGIN_USER:
      return action.payload
    case LOGOUT_USER:
      return initialState
    default:
      return state
  }
}
